import { useContext, useMemo, useState } from 'react';
import t from 'react-translate';
import moment from 'moment';
import { useSelector } from 'react-redux';
import prodPathReplace from 'shared/prod-path-rewrite';

import NvModal, { ModalType } from 'shared/components/nv-modal';
import MentoringProgramContext from '../../context';
import { selectGuidesForProfileCompletionOrReviewMilestoneByRole } from 'redux/selectors/mentorship-program-guides';
import { CompletionStatus, MentoringProgramEnrollment } from 'redux/schemas/models/mentoring-program-enrollments';
import { MentoringProgramGuide } from 'redux/schemas/models/mentoring-program-guides';
import GuidanceSection from './guidance-section';

const MODAL_WIDTH = 440;

const SETTINGS = {
  [CompletionStatus.IN_PROGRESS]: {
    image: 'remaining-questions',
    label: 'IN_PROGRESS',
  },
  [CompletionStatus.COMPLETED]: {
    image: 'all-done',
    label: 'COMPLETED',
  },
};

const getTimeLeft = (date: string, unit: moment.unitOfTime.Diff = 'day') => moment(date).diff(moment(), unit);

const getUpcomingDateDescription = (
  date: string,
  translationBase: any,
  label: string,
) => {
  let timeLeft = getTimeLeft(date);
  let timeUnit = translationBase.TIME_UNIT.DAY();

  if (timeLeft === 0) {
    timeUnit = translationBase.TIME_UNIT.HOUR();
    if (getTimeLeft(date, timeUnit) < 1) {
      timeUnit = translationBase.TIME_UNIT.MINUTE();
    }
  } else if (timeLeft > 30) {
    timeUnit = translationBase.TIME_UNIT.MONTH();
    const daysLeft = timeLeft % 30;
    if (daysLeft > 0) {
      const monthsLeft = Math.floor(timeLeft / 30);
      const daysUnit = translationBase.TIME_UNIT.DAY();
      return translationBase[label].MATCH_DATE_UPCOMING_MONTHS_DAYS(
        daysLeft,
        daysUnit,
        monthsLeft,
        timeUnit,
      );
    }
  }
  timeLeft = getTimeLeft(date, timeUnit);
  return translationBase[label].MATCH_DATE_UPCOMING(timeLeft, timeUnit);
};

const getMatchDateDescription = (
  matchAnnouncementDate: string,
  translationBase: any,
  label: string,
  guidanceEnabled: boolean,
  setShowGuidanceSection: (show: boolean) => void,
) => {
  const isMatchPast = moment().isAfter(matchAnnouncementDate);
  const isMatchUpcoming = moment(matchAnnouncementDate).isAfter(moment());
  const matchDateDescription = isMatchPast
    ? translationBase[label].MATCH_DATE_PASSED()
    : getUpcomingDateDescription(matchAnnouncementDate, translationBase, label);
  let description = matchAnnouncementDate
    ? matchDateDescription
    : translationBase[label].NO_MATCH_DATE();
  if (guidanceEnabled && label === 'COMPLETED' && (isMatchUpcoming || !matchAnnouncementDate)) {
    setShowGuidanceSection(true);
    description = (
      <>
        {description}
        {translationBase[label].GUIDE_DESCRIPTION()}
      </>
    );
  }
  return description;
};

type RequiredQuestionsModalProps = {
  show: boolean;
  remainingQuestions: number;
  onClose?: () => void;
};

const RequiredQuestionsModal = ({
  show,
  remainingQuestions,
  onClose,
}: RequiredQuestionsModalProps) => {
  const translationBase = t.MENTORING_PROGRAMS.PARTICIPANT_HOME.REMAINING_QUESTIONS_MODAL;
  const status = remainingQuestions ? CompletionStatus.IN_PROGRESS : CompletionStatus.COMPLETED;
  const { mentoringProgram } = useContext(MentoringProgramContext);
  const {
    matchAnnouncementDate,
    mentorshipProgramEnrollment: enrollmentId,
    guidanceEnabled,
  } = mentoringProgram || {};
  const { image, label } = SETTINGS[status];
  const [showGuidanceSection, setShowGuidanceSection] = useState(false);

  const src = prodPathReplace(`images/${image}.svg`);
  const title = translationBase[label].TITLE(remainingQuestions ?? null);
  const description = useMemo(
    () =>
      getMatchDateDescription(
        matchAnnouncementDate,
        translationBase,
        label,
        guidanceEnabled,
        setShowGuidanceSection
      ),
    [matchAnnouncementDate, translationBase, label, guidanceEnabled]
  );
  const mentoringProgramEnrollment: MentoringProgramEnrollment = useSelector(state => (
      state.models.mentoringProgramEnrollments[enrollmentId]
    ));
  const { role } = mentoringProgramEnrollment || {};
  const guides: MentoringProgramGuide[] = useSelector(
    selectGuidesForProfileCompletionOrReviewMilestoneByRole(role.name, status)
  );

  return (
    <NvModal
      show={show}
      width={MODAL_WIDTH}
      height='unset'
      headerBgColor='transparent'
      bodyClassName='p-0'
      type={ModalType.FIXED}
      onClose={onClose}
      header=''
      body={(
        <div className='d-flex flex-column align-items-center text-center'>
          <img src={src} alt={title} />
          <div className='w-100 px-6 py-5'>
            <div className='page-title mb-2'>{title}</div>
            <div className='text-large-body'>{description}</div>
            {showGuidanceSection && guides?.length ? (
              <GuidanceSection
                guides={guides}
                className='mt-2 d-flex justify-content-center'
              />
            ) : null}
          </div>
        </div>
      )}
    />
  );
};

export default RequiredQuestionsModal;
